import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { userApi, galleryApi } from '../api/client'
import { useAuth } from '../contexts/AuthContext'

export default function UserProfile() {
  const { id } = useParams()
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // 粉丝/关注列表弹窗：null | 'followers' | 'following'
  const [listType, setListType] = useState(null)
  const [listUsers, setListUsers] = useState([])
  const [listLoading, setListLoading] = useState(false)

  const openList = async (type) => {
    setListType(type)
    setListUsers([])
    setListLoading(true)
    try {
      const { data } = type === 'followers'
        ? await userApi.getFollowers(id)
        : await userApi.getFollowing(id)
      setListUsers(data.users || [])
    } catch {
      setListUsers([])
    } finally {
      setListLoading(false)
    }
  }

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true)
        const { data } = await userApi.get(id)
        setProfile(data)
      } catch (err) {
        setError(err.response?.data?.error || err.message || '加载失败')
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [id])

  const toggleVisibility = async (item) => {
    try {
      const { data } = await galleryApi.setVisibility(item.id, !item.isPublic)
      setProfile((prev) => ({
        ...prev,
        items: prev.items.map((i) => (i.id === item.id ? { ...i, isPublic: data.isPublic } : i)),
      }))
    } catch (err) {
      alert(err.response?.data?.error || '操作失败')
    }
  }

  const handleFollow = async () => {
    try {
      const { data } = await userApi.follow(id)
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              isFollowing: data.following,
              stats: {
                ...prev.stats,
                followers: data.following ? prev.stats.followers + 1 : Math.max(0, prev.stats.followers - 1),
              },
            }
          : prev
      )
    } catch (err) {
      alert(err.response?.data?.error || '关注失败')
    }
  }

  if (loading) return <div className="py-20 text-center text-cocoa-light">加载中…</div>
  if (error || !profile) {
    return (
      <div className="mx-auto max-w-7xl py-20 text-center text-cocoa-light">
        {error || '用户不存在'}
      </div>
    )
  }

  const isAuthor = user?.id === profile.user.id

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blush text-2xl font-bold text-white">
            {(profile.user.username || profile.user.email || '?')[0]}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-cocoa">
              {profile.user.username || profile.user.email}
            </h1>
            <div className="mt-1 flex gap-4 text-sm text-cocoa-light">
              <button onClick={() => openList('followers')} className="transition hover:text-cocoa hover:underline">
                {profile.stats.followers} 粉丝
              </button>
              <button onClick={() => openList('following')} className="transition hover:text-cocoa hover:underline">
                {profile.stats.following} 关注
              </button>
              <span>{profile.items.length} 作品</span>
            </div>
          </div>
          {!isAuthor && (
            <button
              onClick={handleFollow}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                profile.isFollowing
                  ? 'border border-lavender/60 text-cocoa hover:bg-lavender/30'
                  : 'bg-blush text-white shadow-soft hover:bg-blush-dark'
              }`}
            >
              {profile.isFollowing ? '已关注' : '+ 关注'}
            </button>
          )}
        </div>
      </section>

      {profile.items.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center rounded-[2rem] border-2 border-dashed border-lavender/40 bg-white text-center shadow-card">
          <p className="text-cocoa-light">该用户还没有发布作品</p>
        </div>
      ) : (
        <div className="columns-2 gap-4 md:columns-3 lg:columns-4">
          {profile.items.map((item) => (
            <div key={item.id} className="mb-4 break-inside-avoid overflow-hidden rounded-2xl border border-lavender/30 bg-white shadow-sm transition hover:shadow-card">
              <Link to={`/gallery/${item.id}`}>
                <img src={item.imageUrl} alt={`work-${item.id}`} className="w-full" loading="lazy" />
              </Link>
              <div className="p-3">
                {item.title && <p className="mb-1 text-sm font-medium text-cocoa">{item.title}</p>}
                <div className="flex items-center justify-between text-xs text-cocoa-light">
                  <div>
                    {item.brand && <span className="font-medium text-cocoa">{item.brand}</span>}
                    {item.brand && ' · '}
                    {item.width}×{item.height}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-[10px] text-cocoa-light">
                      <span>♥</span>
                      <span>{item.likeCount}</span>
                    </div>
                    {isAuthor && (
                      <button
                        onClick={() => toggleVisibility(item)}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition ${
                          item.isPublic
                            ? 'bg-blush text-white'
                            : 'border border-lavender/60 text-cocoa hover:bg-lavender/30'
                        }`}
                      >
                        {item.isPublic ? '公开' : '私有'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 粉丝/关注列表弹窗 */}
      {listType && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setListType(null)}
        >
          <div
            className="max-h-[70vh] w-full max-w-md overflow-y-auto rounded-[2rem] bg-white p-6 shadow-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-cocoa">
                {listType === 'followers' ? '粉丝（谁关注了 TA）' : '关注（TA 关注了谁）'}
              </h3>
              <button
                onClick={() => setListType(null)}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
              >
                关闭
              </button>
            </div>
            {listLoading ? (
              <p className="text-sm text-cocoa-light">加载中…</p>
            ) : listUsers.length === 0 ? (
              <div className="rounded-2xl bg-cream-100 py-8 text-center">
                <p className="text-sm text-cocoa-light">
                  {listType === 'followers' ? '还没有人关注 TA' : 'TA 还没有关注任何人'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {listUsers.map((u) => (
                  <Link
                    key={u.id}
                    to={`/user/${u.id}`}
                    onClick={() => setListType(null)}
                    className="flex items-center gap-3 rounded-2xl border border-lavender/30 bg-cream-50 p-3 transition hover:bg-lavender/20"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blush text-sm font-bold text-white">
                      {(u.username || u.email || '?')[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-cocoa">
                        {u.username || u.email}
                      </p>
                      <p className="truncate text-xs text-cocoa-light">
                        {u.username ? u.email : ''}
                        {u.followedAt ? ` · ${new Date(Number(u.followedAt)).toLocaleDateString('zh-CN')} 关注` : ''}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
